import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Globe2, LoaderCircle, RotateCcw, Scissors, Share2, ShieldCheck, Target, Trophy, WifiOff } from 'lucide-react';
import { api, publicApi, loadLocal, saveLocal } from './api';
import { usePlatform } from './PlatformContext';
import { InlineError } from './AuthDialog';
import { SliceSurface, SHAPE_COLORS } from '../game/SliceSurface';
import { ResultReveal } from '../game/ResultReveal';
import { polygonPath } from '../game/geometry.mjs';
import { formatPercent, scoreCut } from '../game/rules.mjs';
import { haptic, playSound } from '../game/sound';
import { ShareResultDialog } from '../brand/ShareResultDialog';
import { trackOnce } from '../lib/analytics';
import { isDcCompletedToday, checkStreakMilestone } from '../lib/streakUtils';
import { ConfettiCanvas } from '../lib/ConfettiCanvas';
import { Button } from '@/components/ui/button';
import { cachedChallenge, cacheChallenge, fetchChallenge, fetchDailyState, playerIdentity, utcDate } from './dailyData';
import { DailyCanonicalSkeleton } from './DailyLoading';
import { DailyStreakCard } from './DailyStreakCard';
import { GuestProfileCard } from './GuestProfileCard';
import { CrossGameNudge } from './CrossGameNudge';

/** Compute Dead Center streak from an array of history days (newest-first). */
function computeDcStreak(days) {
  if (!Array.isArray(days) || !days.length) return 0;
  const dateSet = new Set(days.map(d => d.date));
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  // Allow streak to start from today OR yesterday (player may not have finished today)
  const todayStr = today.toISOString().slice(0, 10);
  const check    = new Date(today);
  if (!dateSet.has(todayStr)) check.setUTCDate(check.getUTCDate() - 1);
  let streak = 0;
  while (dateSet.has(check.toISOString().slice(0, 10))) {
    streak++;
    check.setUTCDate(check.getUTCDate() - 1);
  }
  return streak;
}

const EMPTY = { attempts: [], completed: false, best: null };
export function DailySlice({ onPlayEndless, sound }) {
  const { user, setUser, guest, setGuest, scope, authLoading, day, navigate, version, setDaily } = usePlatform();
  const [data, setData] = useState(() => { const challenge = cachedChallenge(); return challenge ? { challenge, state: EMPTY } : null; });
  const [checking, setChecking] = useState(true);
  const [verifiedKey, setVerifiedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('intro');
  const [busy, setBusy] = useState(false);
  const [provisional, setProvisional] = useState(null);
  const [offline, setOffline] = useState(false);
  const [pending, setPending] = useState(null);
  const [hint, setHint] = useState('');
  // Provisional attempt shown immediately on cut; cleared when server confirms.
  const [provisionalAttempt, setProvisionalAttempt] = useState(null);
  const [shareData, setShareData] = useState(null);
  const active = useRef(true);
  const fetchSerial = useRef(0);
  const submitting = useRef(false);
  /* Cross-game nudge — fires once, 2.5 s after completion */
  const [psNudgeOpen,  setPsNudgeOpen]  = useState(false);
  const [dcStreak,     setDcStreak]     = useState(null);
  const [psStreak,     setPsStreak]     = useState(null);
  const [bothComplete, setBothComplete] = useState(false);
  const psNudgeFired = useRef(false);

  /* ── Streak milestone celebration ── */
  const [psMilestoneStreak, setPsMilestoneStreak] = useState(null);
  const psMilestoneShownRef = useRef(false);
  const identity = playerIdentity(user, guest);
  const identityKey = `${user?.id || 'guest'}:${identity}:${scope}:${day}`;
  const identityRef = useRef(identityKey);
  if (identityRef.current !== identityKey) {
    identityRef.current = identityKey;
    fetchSerial.current += 1;
  }
  const cacheKey = `perfect-slice.daily-cache.v1:${identity}`;
  const previousData = useRef(data);
  previousData.current = data;
  const state = data?.state || EMPTY;

  /* ── Cross-game nudge: open after both fetches settle (min 2.5 s, cap 4 s) ── */
  useEffect(() => {
    if (!state.completed || psNudgeFired.current) return;
    psNudgeFired.current = true;
    let alive = true;
    // Fetch DC history (streak + completion) and PS summary in parallel.
    // Use `api` (NOT publicApi) so the guest-session cookie is sent and the
    // backend returns THIS user's history instead of an empty anonymous result.
    const historyFetch = api('/deadcenter/history')
      .then(days => {
        if (!alive || !Array.isArray(days)) return;
        setDcStreak(computeDcStreak(days));
        if (isDcCompletedToday(days)) setBothComplete(true);
      })
      .catch(() => {});
    const summaryFetch = api('/daily/summary')
      .then(d => { if (alive && typeof d?.streak?.current === 'number') setPsStreak(d.streak.current); })
      .catch(() => {});
    // Open after min 2.5 s AND after fetches settle; never wait more than 4 s total
    const minDelay = new Promise(r => setTimeout(r, 2500));
    const maxDelay = new Promise(r => setTimeout(r, 4000));
    Promise.all([minDelay, Promise.race([Promise.all([historyFetch, summaryFetch]), maxDelay])])
      .then(() => { if (alive) setPsNudgeOpen(true); });
    return () => { alive = false; };
  }, [state.completed]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Milestone check: fires when psStreak is first set after completion ── */
  useEffect(() => {
    if (!state.completed || psStreak === null || psMilestoneShownRef.current) return;
    psMilestoneShownRef.current = true;
    const ms = checkStreakMilestone('ps', psStreak);
    if (ms) setPsMilestoneStreak(ms);
  }, [psStreak, state.completed]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Milestone toast: fires when psMilestoneStreak is set ── */
  useEffect(() => {
    if (!psMilestoneStreak) return;
    const legendary = psMilestoneStreak === 30;
    toast.custom(() => (
      <div className="milestone-toast milestone-toast--ps" data-testid="milestone-toast">
        <span className="milestone-toast-icon" aria-hidden="true">{legendary ? '🏆' : '🔥'}</span>
        <div className="milestone-toast-content">
          <strong className="milestone-toast-title">{psMilestoneStreak}-Day Streak!</strong>
          <span className="milestone-toast-sub">
            {psMilestoneStreak} days of Perfect Slice in a row.
            {legendary ? " You're a legend." : ''}
          </span>
        </div>
      </div>
    ), { duration: 5500, id: `milestone-ps-${psMilestoneStreak}` });
  }, [psMilestoneStreak]); // eslint-disable-line react-hooks/exhaustive-deps
  const canonical = data?.challenge;
  const attemptsReady = !authLoading && !checking && !offline && canonical?.date === day && verifiedKey === `${identity}:${canonical?.date}:${canonical?.id}`;
  const challenge = useMemo(() => canonical ? ({ seed: canonical.id, round: canonical.number, target: canonical.target, colorIndex: canonical.colorIndex, polygon: canonical.geometry.map(([x, y]) => [x * 360, y * 320]) }) : null, [canonical]);
  const result = phase === 'result' ? (state.completed ? state.best : (provisionalAttempt || state.attempts.at(-1))) : null;
  // Count that includes a provisional attempt not yet confirmed by the server
  const effectiveAttemptCount = state.attempts.length + (provisionalAttempt && !state.completed ? 1 : 0);
  const bestIndex = state.best ? state.attempts.findIndex(a => a.attempt_number === state.best.attempt_number) : -1;
  const pendingKey = data ? `perfect-slice.daily-pending.v1:${data.challenge.id}` : null;
  const store = useCallback(next => {
    setData(next); previousData.current = next; setDaily(next);
    cacheChallenge(next.challenge);
    // Private offline result snapshots remain owner-scoped and separate from
    // the new public challenge/leaderboard cache. Never unlock from these.
    if (next.state?.attempts?.length) {
      const snapshot = { challenge: next.challenge, state: next.state, user: next.user, guest: next.guest };
      saveLocal(cacheKey, snapshot); saveLocal('perfect-slice.daily-last.v1', snapshot);
    }
  }, [cacheKey, setDaily]);
  const load = useCallback(async (quiet = false) => {
    const serial = ++fetchSerial.current;
    if (!quiet && !previousData.current) setLoading(true);
    setChecking(true); setVerifiedKey(null); setError('');
    const currentRequest = () => active.current && serial === fetchSerial.current;
    // Public geometry starts independently of bootstrap/auth/attempt checks.
    const canonicalRequest = fetchChallenge(quiet).then(challenge => {
      if (currentRequest()) {
        const previous = previousData.current;
        const next = previous?.challenge.id === challenge.id ? { ...previous, challenge } : { challenge, state: EMPTY };
        setData(next); previousData.current = next; setLoading(false);
        if (previous && previous.challenge.id !== challenge.id) { setPhase('intro'); setPending(null); }
      }
      return challenge;
    });
    canonicalRequest.catch(err => { if (currentRequest()) { setError(err.message); setLoading(false); } });
    if (authLoading) return;
    try {
      const privateRequest = (async () => {
        // Only retry an outstanding guest claim; normal sign-in already claims.
        const legacyGuestCache = loadLocal('perfect-slice.daily-cache.v1:guest');
        if (user?.alias_confirmed && legacyGuestCache?.state?.attempts?.length) {
          await api('/daily/claim', { method: 'POST', body: {} });
          saveLocal('perfect-slice.daily-cache.v1:guest', null);
        }
        return fetchDailyState(identity);
      })();
      const response = await privateRequest;
      let challenge = cachedChallenge();
      if (challenge?.id !== response.challenge_id || challenge?.date !== response.date) {
        try { challenge = await canonicalRequest; }
        catch { challenge = cachedChallenge(); }
      }
      if (!currentRequest()) return;
      if (challenge?.id !== response.challenge_id || challenge?.date !== response.date) challenge = await fetchChallenge(true);
      if (!currentRequest()) return;
      if (challenge.id !== response.challenge_id || response.date !== utcDate()) throw new Error('A new Daily Slice is ready. Reconnect to check your attempts.');
      const sameChallenge = previousData.current?.challenge.id === challenge.id;
      const next = { challenge, state: response.state, user: response.user, guest: response.guest || null, my_entry: null };
      store(next); setOffline(false); setError('');
      setUser(current => current?.id === response.user?.id && current?.alias === response.user?.alias && current?.alias_confirmed === response.user?.alias_confirmed ? current : response.user);
      if (!response.user?.alias_confirmed && response.guest?.id !== guest?.id) setGuest?.(response.guest || null);
      setVerifiedKey(`${playerIdentity(response.user, response.guest)}:${response.date}:${response.challenge_id}`);
      const savedPending = loadLocal(`perfect-slice.daily-pending.v1:${challenge.id}`);
      const alreadySaved = savedPending && response.state.attempts.some(a => a.request_id === savedPending.request_id);
      if (alreadySaved) saveLocal(`perfect-slice.daily-pending.v1:${challenge.id}`, null);
      setPending(savedPending && !alreadySaved && !response.state.completed ? savedPending : null);
      if (response.state.attempts.length) setPhase(current => current === 'play' && sameChallenge && !response.state.completed ? 'play' : 'result');
      else if (!quiet) setPhase(current => current === 'play' && sameChallenge ? 'play' : 'intro');
      // Rank is optional enrichment, never a dependency of the playable shape.
      if (response.state.completed && (response.user?.alias_confirmed || response.guest)) api('/daily/rank').then(rank => {
        if (currentRequest() && rank.challenge_id === challenge.id) setData(current => current?.challenge.id === challenge.id ? { ...current, my_entry: rank.my_entry } : current);
      }).catch(() => { /* Rank is optional; official attempts remain valid and the leaderboard can retry. */ });
    } catch (err) {
      if (!currentRequest()) return;
      setError(err.message); setOffline(true);
      const cached = loadLocal(cacheKey);
      const ownsCache = playerIdentity(cached?.user, cached?.guest) === identity;
      if (ownsCache && cached?.state?.attempts?.length && cached.challenge?.date === utcDate() && (!scope || cached.challenge.scope === scope) && (!previousData.current || cached.challenge.id === previousData.current.challenge.id)) { setData({ ...cached, my_entry: null }); setPhase('result'); }
    } finally { if (currentRequest()) { setChecking(false); setLoading(false); } }
  }, [store, cacheKey, setUser, identity, user?.alias_confirmed, guest?.id, setGuest, scope, authLoading]);
  useEffect(() => {
    active.current = true;
    submitting.current = false; setBusy(false); setProvisional(null); setPending(null);
    load();
    return () => { active.current = false; fetchSerial.current += 1; };
  }, [load, version, identityKey]);
  useEffect(() => {
    const focus = () => { if (!submitting.current && document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', focus);
    window.addEventListener('online', focus);
    return () => { document.removeEventListener('visibilitychange', focus); window.removeEventListener('online', focus); };
  }, [load]);
  const submit = async (payload, split = null) => {
    if (submitting.current || !attemptsReady) return;
    const serial = ++fetchSerial.current;
    const isCurrent = () => active.current && serial === fetchSerial.current && identityKey === identityRef.current && data.challenge.date === utcDate();
    submitting.current = true; setBusy(true); setError(''); setHint('');

    // ── Optimistic local scoring ──────────────────────────────────────
    // When a fresh cut is provided (split != null), compute the score locally
    // and immediately show the result. The server remains authoritative.
    let localAttempt = null;
    if (split) {
      const localScore = scoreCut(challenge.target, split.percent);
      localAttempt = {
        // Include split geometry so SliceShape (result.remainder/.selected/.normal) works
        ...split,
        // Score fields (overwrite split.percent with scored percent, add points/tier/etc.)
        ...localScore,
        attempt_number: state.attempts.length + 1,
        request_id: payload.request_id,
      };
      setProvisionalAttempt(localAttempt);
      setPhase('result');          // Show result overlay immediately

      // Fire sound + haptic instantly (keyed to local score, not server)
      if (sound) {
        playSound('slice');
        if (localScore.difference <= 1 + 1e-9) {
          playSound(localScore.tier === 'legendary' ? 'legendary' : localScore.difference <= .5 + 1e-9 ? 'celebrate' : 'good');
        }
      }
      haptic(localScore.difference <= .5 + 1e-9 ? 'perfect' : localScore.difference <= 1 + 1e-9 ? 'excellent' : 'ordinary');
    }
    // ─────────────────────────────────────────────────────────────────

    setProvisional(split); setPending(payload); saveLocal(pendingKey, payload);
    try {
      const response = await api('/daily/attempts', { method: 'POST', body: payload, isCurrent });
      if (!isCurrent()) return;

      const next = { ...data, challenge: response.challenge, state: response.state, guest: response.guest || guest || null };
      store(next); setPhase('result'); setPending(null); saveLocal(pendingKey, null); setOffline(false);
      if (!user?.alias_confirmed && response.guest?.id !== guest?.id) setGuest?.(response.guest || null);
      const cut = response.state.attempts.find(a => a.request_id === payload.request_id) || response.state.attempts.at(-1);

      // ── Reconcile local vs server score ──────────────────────────────
      if (localAttempt && !response.replayed) {
        const diff = Math.abs(localAttempt.points - cut.points);
        if (diff > 2) {
          toast('Score adjusted after verification.', { duration: 3000 });
          console.warn(
            `[DailySlice] score discrepancy attempt=${payload.attempt_number} ` +
            `local=${localAttempt.points} server=${cut.points} diff=${diff}`,
          );
        }
        // diff <= 2: silently accept server score
      }
      setProvisionalAttempt(null);  // Server data is now authoritative
      // ─────────────────────────────────────────────────────────────────

      if (!response.replayed) {
        const loggedIn = !!user?.alias_confirmed;
        const isFirstAttempt = response.state.attempts.length === 1;
        const attemptNum = response.state.attempts.length;
        // game_start is kept for backward compatibility with existing GA4 custom dimensions.
        // daily_start and level_start are fired by startPlay() / retry() at the true start
        // of each attempt, before the player draws a cut.
        if (isFirstAttempt) trackOnce('game_start', `daily:${response.challenge.id}`, { game_mode: 'daily' });
        const params = { game_mode: 'daily', round_number: response.challenge.number, attempt_count: attemptNum, attempt_number: attemptNum, score: cut.points, error_percent: cut.difference };
        trackOnce('daily_attempt_saved', payload.request_id, params);
        if (response.state.completed) {
          const bestDiff = response.state.best.difference;
          const completionParams = {
            game_mode: 'daily',
            round_number: response.challenge.number,
            attempt_number: response.state.attempts.length,
            attempt_count: response.state.attempts.length,
            score: response.state.best.points,
            error_percent: bestDiff,
            difference_percent: parseFloat(bestDiff.toFixed(2)),
            accuracy: parseFloat(Math.max(0, 100 - bestDiff).toFixed(2)),
            logged_in: loggedIn,
          };
          // Legacy event kept for GA4 custom dimensions / key events already configured.
          trackOnce('daily_complete', response.challenge.id, { ...params, score: response.state.best.points, error_percent: bestDiff });
          // Google-recommended events fire at the same moment.
          trackOnce('level_end',  `dle:${response.challenge.id}`, completionParams);
          trackOnce('post_score', `dps:${response.challenge.id}`, completionParams);
        }
        // Note: Sound + haptic already fired immediately on cut (above). Skip here.
      }
      if (next.state.completed) {
        api('/daily/rank').then(board => { if (isCurrent() && board.challenge_id === next.challenge.id) setData(current => current?.challenge.id === next.challenge.id ? { ...current, my_entry: board.my_entry } : current); }).catch(() => { /* Rank is optional; official attempts remain valid and the leaderboard can retry. */ });
      }
    } catch (err) {
      if (!isCurrent()) return;
      if ([409, 422, 400].includes(err.status)) {
        // Hard rejection: clear provisional, revert to play so the player can retry cleanly
        setProvisionalAttempt(null);
        setPending(null); saveLocal(pendingKey, null);
        if (err.status === 409) { await load(true); setError(err.message); }
        else setError(err.message);
        if (localAttempt) setPhase('play');   // Revert optimistic phase change
      } else {
        // Network/server error: keep provisional result visible.
        // The pending retry button will allow the player to resubmit safely.
        if (!localAttempt) {
          // Retry path (no provisional): show the generic error as before.
          setError(`${err.message} This cut may already be saved. Reconnect to check it, or retry the same cut safely.`);
        }
        // If localAttempt exists: provisionalAttempt stays set, pending retry button guides user.
      }
    } finally {
      if (active.current && identityKey === identityRef.current) { submitting.current = false; setBusy(false); setProvisional(null); }
    }
  };
  const onCut = split => {
    if (!data || !attemptsReady || state.completed || busy || pending) return;
    const payload = { challenge_id: data.challenge.id, attempt_number: state.attempts.length + 1, request_id: crypto.randomUUID(), cut_start: [split.start[0] / 360, split.start[1] / 320], cut_end: [split.end[0] / 360, split.end[1] / 320], selected_side: 'left' };
    submit(payload, split);
  };
  const retry = () => {
    // Track the start of the next individual attempt before resetting the view.
    const nextAttemptNum = (state.attempts?.length ?? 0) + 1;
    if (data?.challenge.id && nextAttemptNum <= 3) {
      trackOnce('level_start', `dls:${data.challenge.id}:${nextAttemptNum}`, { game_mode: 'daily', attempt_number: nextAttemptNum, logged_in: !!user?.alias_confirmed });
    }
    setProvisionalAttempt(null);   // clear any provisional before going back to play
    setPhase('play'); setError(''); setHint('');
  };
  // Fires when the player actively starts today's challenge from the intro screen.
  // daily_start fires once per session (deduped); level_start:1 marks attempt 1 beginning.
  const startPlay = () => {
    if (!attemptsReady || !data?.challenge.id) return;
    setPhase('play');
    trackOnce('daily_start', `ds:${data.challenge.id}`, { game_mode: 'daily', logged_in: !!user?.alias_confirmed });
    trackOnce('level_start', `dls:${data.challenge.id}:1`, { game_mode: 'daily', attempt_number: 1, logged_in: !!user?.alias_confirmed });
  };
  const share = () => {
    if (!state.best) return;
    setShareData({ mode: 'daily', number: canonical.number, date: canonical.date, scope: canonical.scope, target: canonical.target, best: state.best, attempts: state.attempts.length, url: window.location.origin + '/perfectslice/daily' });
  };
  if (loading && !data) return <main className="platform-page" data-testid="daily-loading-page"><span className="platform-eyebrow">ONE SHAPE. EVERYONE. TODAY.</span><h1>Daily Slice</h1><DailyCanonicalSkeleton/><Button variant="ghost" className="text-button" data-testid="daily-loading-endless" onClick={onPlayEndless}>Play Endless instead<ArrowRight size={14}/></Button></main>;
  if (!data) return <main className="platform-page"><span className="platform-eyebrow">DAILY SLICE</span><h1>A little pause.</h1><InlineError message={error || 'Today’s slice is not available yet.'} retry={() => load()} testId="daily-load-error"/><button className="primary-button" data-testid="daily-error-endless" onClick={onPlayEndless}>PLAY ENDLESS<ArrowRight size={18}/></button></main>;
  return <>
    {phase === 'intro' && !pending ? <main className="daily-intro platform-page" data-testid="daily-intro">
      <div className="platform-page-top"><button className="back-button" data-testid="daily-back-home-button" onClick={() => navigate('/perfectslice')}><ArrowLeft size={15}/> Back to the game</button><span className="utc-label">RESETS 00:00 UTC</span></div>
      {data.challenge.scope === 'preview' && <span className="preview-pill" data-testid="daily-preview-label">PREVIEW DAILY · NOT THE PRODUCTION BOARD</span>}
      <span className="platform-eyebrow">ONE SHAPE. EVERYONE. TODAY.</span><h1>Daily Slice<span className="daily-number">#{data.challenge.number}</span></h1>
      <p className="daily-intro-description">Same shape. Same target.<br/>Everyone gets three cuts. <b>Your best counts.</b></p>
      {/* Fixed decorative slash, not a solution: never derive it from the target, seed, or cuts. */}
      <svg className="daily-intro-art" data-testid="daily-intro-art" viewBox="0 0 360 320" aria-label="Today’s shared shape"><path d={polygonPath(challenge.polygon)} fill={SHAPE_COLORS[challenge.colorIndex].main}/><path data-testid="daily-intro-decorative-line" aria-hidden="true" d="M30 240L333 94" stroke="var(--paper)" strokeWidth="7" strokeDasharray="5 5"/></svg>
      <p className="daily-intro-target" data-testid="daily-intro-target">Cut off <strong>{challenge.target}%</strong></p>
      {(checking || authLoading) && <div className="daily-attempt-check" data-testid="daily-attempt-check-banner" role="status"><Scissors size={15}/><span data-testid="daily-attempt-check-text">Checking your attempts…</span></div>}
      <InlineError message={error} retry={() => load(true)} testId="daily-intro-error"/>
      <div className="daily-intro-facts"><span><Globe2 size={16}/> Same worldwide</span><span><Target size={16}/> Three scored cuts</span></div>
      <button className="primary-button" data-testid="daily-intro-play-today-button" disabled={!attemptsReady} onClick={startPlay}>PLAY TODAY’S SLICE<ArrowRight size={19}/></button>
      <button className="text-button" data-testid="daily-intro-leaderboard-button" onClick={() => navigate('/perfectslice/leaderboard')}>Today’s leaderboard<Trophy size={15}/></button>
      <p className="daily-no-signup"><ShieldCheck size={14}/> No account needed to play.</p>
    </main> : <main className={`game-main daily-game ${result ? 'has-result persistent-result' : ''} ${state.completed ? 'round-complete' : ''}`} data-testid="daily-game-screen">
      <div className="round-row"><button className="back-button" data-testid="daily-game-back-button" onClick={() => navigate('/perfectslice')}><ArrowLeft size={15}/> Back</button><span className="round-label" data-testid="daily-number">DAILY SLICE #{data.challenge.number}</span><span className="difficulty-label">{data.challenge.scope === 'preview' ? 'PREVIEW' : '00:00 UTC RESET'}</span></div>
      {state.completed && <div className="daily-complete-label" data-testid="daily-complete-label"><CheckCircle2 size={15}/> TODAY’S SLICE COMPLETE</div>}
      <section className="target-block"><span className="target-label">CUT OFF</span><h1 data-testid="daily-target-percent">{challenge.target}<span>%</span></h1><span className="target-subline">{state.best ? `Beat ${formatPercent(state.best.difference)}% off` : 'Three cuts. Your best counts.'}</span></section>
      {(checking || authLoading) && <div className="daily-attempt-check" data-testid="daily-attempt-check-banner" role="status"><Scissors size={15}/><span data-testid="daily-attempt-check-text">Checking your attempts…</span></div>}
      <div className={busy || pending || offline || !attemptsReady ? 'daily-surface-locked' : ''}>
        <SliceSurface disabled={!attemptsReady || busy || !!pending || state.completed} challenge={challenge} result={result || provisional} onCut={onCut} onInvalid={() => setHint('Cut all the way through the shape')}>
          {result && <ResultReveal key={`${data.challenge.id}-${effectiveAttemptCount}`} result={result} latestResult={state.attempts.at(-1) || result} complete={state.completed} bestAttempt={bestIndex + 1}/>} 
          {busy && !provisionalAttempt && <div className="daily-score-pending" data-testid="daily-score-pending-indicator"><LoaderCircle size={22} className="spin"/><span>Checking your cut…</span><small>Scored on the server. Your best counts.</small></div>}
        </SliceSurface>
      </div>
      <div className="attempt-section"><div className="attempt-dots" data-testid="daily-attempts" aria-label={`${effectiveAttemptCount} of 3 attempts used`}>{[0, 1, 2].map(i => <span key={i} className={`attempt-dot ${i < effectiveAttemptCount ? 'used' : ''} ${!result && i === effectiveAttemptCount ? 'current' : ''}`}><i/>{i === bestIndex && <span className="best-dot-marker"/>}</span>)}</div><span className="attempt-label">{state.completed ? 'BEST OF THREE' : `ATTEMPT ${Math.min(3, effectiveAttemptCount + (result ? 0 : 1))} OF 3`}</span></div>
      {offline && <div className="daily-offline"><WifiOff size={14}/> Saved result · reconnect for the latest rank</div>}
      <InlineError message={error} retry={() => load(true)} testId="daily-game-error"/>
      {pending && !busy && <div className="daily-pending-retry"><p>We couldn't verify this result. Your cut is saved on this device until confirmed.</p><button className="primary-button" data-testid="daily-retry-pending-button" onClick={() => submit(pending)}>RETRY SAVING THIS CUT<RotateCcw size={17}/></button></div>}
      {result && <div className="result-panel daily-result-panel" data-testid="daily-result-panel">
        {state.completed ? <><div className="attempt-history">{state.attempts.map((a, i) => <div key={a.request_id} className={`attempt-history-item ${i === bestIndex ? 'is-best' : ''}`} data-testid={`daily-attempt-result-${i + 1}`}><span className="history-attempt">{i === bestIndex ? <><Trophy size={12}/> BEST · {i + 1}</> : `ATTEMPT ${i + 1}`}</span><strong>{a.points}<small> pts</small></strong><span>{formatPercent(a.difference)}% off</span></div>)}</div>
          {(user?.alias_confirmed || data.guest || guest) && <div className="daily-my-rank" data-testid="daily-my-rank">{data.my_entry ? <><Trophy size={18}/><strong>#{data.my_entry.rank}</strong> today · {user?.alias_confirmed ? user.alias : (data.guest || guest).alias}</> : <>Your score is saved. Refresh to check your rank.</>}</div>}
          {!user?.alias_confirmed && <GuestProfileCard profile={data.guest || guest}/>}
          {(user?.alias_confirmed || data.guest || guest) && attemptsReady && <DailyStreakCard key={`${identity}:${data.challenge.id}`} challengeId={data.challenge.id}/>}
          <div className="result-actions"><button className="primary-button" data-testid="daily-complete-view-leaderboard-button" onClick={() => navigate('/perfectslice/leaderboard')}>VIEW LEADERBOARD<Trophy size={17}/></button><button className="share-button" data-testid="daily-complete-share-result-button" aria-label="Share Daily result" onClick={share}><Share2 size={18}/></button></div>
          <button className="text-button" data-testid="daily-complete-play-endless-button" onClick={onPlayEndless}>Play Endless<ArrowRight size={15}/></button>
        </> : <>{effectiveAttemptCount >= 3 && busy ? <div className="best-attempt" data-testid="daily-verifying-final"><LoaderCircle size={14} className="spin" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.3rem' }}/> Verifying your final cut…</div> : effectiveAttemptCount === 2 ? <div className="best-attempt" data-testid="daily-final-cut-reminder">One cut left to join today’s leaderboard. Your best score stays safe.</div> : state.best ? <div className="best-attempt">Next up: <b>beat {formatPercent(state.best.difference)}% off</b></div> : null}<div className="result-actions">{busy && provisionalAttempt ? (<button className="primary-button" data-testid="daily-try-again-button" disabled aria-busy="true">VERIFYING SCORE…<LoaderCircle size={16} className="spin" style={{ marginLeft: '0.4rem' }}/></button>) : effectiveAttemptCount === 1 ? (<button className="primary-button" data-testid="daily-try-again-button" disabled={offline || !!pending || !attemptsReady} onClick={retry}>ATTEMPT 2 OF 3<ArrowRight size={17}/></button>) : effectiveAttemptCount === 2 ? (<button className="primary-button" data-testid="daily-try-again-button" disabled={offline || !!pending || !attemptsReady} onClick={retry}>ATTEMPT 3 OF 3<ArrowRight size={17}/></button>) : null}</div><button className="text-button" data-testid="daily-pause-endless-button" onClick={onPlayEndless}>Play Endless · Daily progress is saved<ArrowRight size={14}/></button></>}
        {!state.completed && !user?.alias_confirmed && attemptsReady && <GuestProfileCard profile={data.guest || guest} prefix="daily-attempt-guest"/>}
      </div>}
      {!result && !busy && !pending && attemptsReady && <div className="ready-area"><div className="cut-hint" aria-live="polite">{hint || (state.best ? `Beat ${formatPercent(state.best.difference)}% off` : 'Drag across the shape to make one straight cut')}</div><span className="preview-hint">The highlighted piece is your slice. Three scored attempts.</span></div>}
    </main>}
    <ShareResultDialog data={shareData} onClose={() => setShareData(null)}/>
    <CrossGameNudge
      open={psNudgeOpen}
      onClose={() => setPsNudgeOpen(false)}
      fromGame="perfect-slice"
      psStreak={psStreak}
      dcStreak={dcStreak}
      bothComplete={bothComplete}
      onPlayEndless={onPlayEndless}
    />
    {psMilestoneStreak && (
      <ConfettiCanvas duration={3800} onDone={() => setPsMilestoneStreak(null)} />
    )}
  </>;
}
